import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { ensureLoaded } from '@/lib/ensure-loaded';
import { reviewQueueQuery } from '@/services/review';
import { myRoleQuery } from '@/services/team-members';
import { parseKindParam } from '@/features/review/kind-param';
import { parseReviewSearch, type ReviewSearchInput } from '@/features/review/queue-filters';
import { ReviewPage } from '@/features/review/review-page';
import { ClientsReview } from '@/features/operator/clients-review';
import { useTeamFromLink } from '@/features/operations/operator/team-from-link';
import { RoutePending } from '@/components/app/route-pending';

export const Route = createFileRoute('/_authed/review/$kind')({
  // A stage's slug; its kind's own name ("/review/influencer") redirects to the stage; anything else is not found.
  params: { parse: parseKindParam, stringify: ({ kind }) => ({ kind }) },
  validateSearch: (search: ReviewSearchInput & SearchSchemaInput) => parseReviewSearch(search),
  // The whole queue is one request, and her role decides what she may do with it; prefetch both so the page never waterfalls
  // (a read only cancelled — the shell watches both — is waited for again: `ensureLoaded`).
  loader: ({ context: { queryClient, team } }) =>
    team ? Promise.all([ensureLoaded(queryClient, reviewQueueQuery(team.id)), ensureLoaded(queryClient, myRoleQuery(team.id)).catch(() => null)]) : undefined,
  component: ReviewRoute,
});

function ReviewRoute() {
  const { kind } = Route.useParams();
  const search = Route.useSearch();
  // A link that names its team (`team=`: "Your turn to sign") opens on that team: switched first, then read.
  if (useTeamFromLink() === 'switching') return <RoutePending />;
  // An operator never meets the client's decisions here: a link to one of hers says so (`ClientsReview`).
  return (
    <ClientsReview slug={kind} search={search}>
      <ReviewPage slug={kind} search={search} />
    </ClientsReview>
  );
}
